/**
 * BeforeCheckInDialog — Ted 09-24 / 09-29 (lane LC). The LOUD moment.
 *
 * Mirrors Quick Book's "Before you book" panel (#1067) at the door, as a
 * blocking step: every flag the registry alerts at check-in is listed with
 * its pill and its sentence and acknowledged ONE BY ONE; a Do Not Admit dog
 * is a stop with no checkbox; a dog the heat rule applies to is asked "Is
 * she in heat?" and only No lets her in. Check In stays disabled until every
 * box is ticked and every question answered — and when every dog in the
 * household is stopped, there is nothing to check in and the button says so.
 *
 * Every exit is explicit. Escape and "Not now" leave every dog checked OUT;
 * nothing here checks a dog in but the primary button, which hands the
 * surface the per-booking decisions (`arrival_verified`, `heat_confirmed`)
 * its POSTs should carry. Focus lands on the SAFE choice.
 *
 * One component, mounted by every check-in surface through
 * `useCheckInInterrupt` (Front Desk, dashboard arrivals, Bookings — single
 * and "Check in all"). Label guard (#1103): every input has `id` and `name`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { DogFlagChips } from '../dog-first/DogFlagChips.js';
import {
  joinNames,
  resolveInterrupt,
  type CheckInInterruptPlan,
  type HeatAnswer,
  type InterruptItem,
  type InterruptOutcome,
} from './check-in-interrupt.js';
import './CheckInInterrupt.css';

export interface CheckInInterruptRequest {
  /** Dedupe key — the booking id, or the household's customer id for "Check in all". */
  key: string;
  plan: CheckInInterruptPlan;
  /** Runs with the decisions once the desk has answered everything. */
  onProceed: (outcome: InterruptOutcome) => void | Promise<void>;
  onCancel?: () => void;
}

interface BeforeCheckInDialogProps {
  request: CheckInInterruptRequest;
  onProceed: (outcome: InterruptOutcome) => void;
  onCancel: () => void;
}

function formatDeclaredAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return ` on ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/** The sentence beside an acknowledgement — the transport one is the arrival confirmation itself. */
function itemText(item: InterruptItem): string {
  if (item.arrivalConfirm) {
    const who = item.dogNames.length > 0 ? joinNames(item.dogNames) : 'this dog';
    return `${who} ${item.dogNames.length > 1 ? 'are' : 'is'} physically here — I have seen ${item.dogNames.length > 1 ? 'them' : 'the dog'}. Your name and the time are recorded.`;
  }
  const subject = item.dogName ?? (item.flag.scope === 'stay' && item.dogNames.length > 0 ? joinNames(item.dogNames) : null);
  return subject ? `${subject}: ${item.flag.detail}` : item.flag.detail;
}

export function BeforeCheckInDialog({ request, onProceed, onCancel }: BeforeCheckInDialogProps): JSX.Element {
  const { plan } = request;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [acked, setAcked] = useState<Set<string>>(() => new Set());
  const [heat, setHeat] = useState<Record<string, HeatAnswer | undefined>>({});

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const { complete, outcome } = useMemo(() => resolveInterrupt(plan, { acked, heat }), [plan, acked, heat]);
  const dogs = joinNames(plan.rows.flatMap((r) => [...r.dogNames]));
  const owner = plan.rows[0]?.customerName ?? '';
  const proceedCount = outcome.proceed.length;
  const canProceed = complete && proceedCount > 0;
  const onlyStops = plan.items.length === 0 && plan.heatChecks.length === 0 && plan.stops.length > 0;

  const toggle = (id: string, on: boolean) =>
    setAcked((curr) => {
      const next = new Set(curr);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkin-interrupt-title"
      data-testid="checkin-interrupt-dialog"
      data-interrupt-key={request.key}
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onCancel();
      }}
    >
      <div className="ferro-blocking-dialog__panel ferro-checkin-interrupt">
        <p className={`ferro-checkin-interrupt__eyebrow${onlyStops ? ' ferro-checkin-interrupt__eyebrow--danger' : ''}`}>
          {onlyStops ? 'Cannot check in' : 'Before you check in'}
        </p>
        <h2 id="checkin-interrupt-title">
          {dogs}
          {owner ? ` — ${owner}` : ''}
        </h2>

        {plan.stops.length > 0 ? (
          <ul className="ferro-checkin-interrupt__list" aria-label="Do Not Admit">
            {plan.stops.map((stop) => (
              <li
                key={`${stop.bookingId}:${stop.dogName}`}
                className="ferro-checkin-interrupt__stop"
                data-testid={`checkin-interrupt-stop-${stop.bookingId}`}
              >
                <DogFlagChips flags={[stop.flag]} scope="dog" testIdPrefix="checkin-interrupt-" />
                <span>
                  <strong>{stop.dogName} cannot be checked in.</strong> {stop.flag.detail}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {plan.items.length > 0 ? (
          <>
            <p className="ferro-checkin-interrupt__lead">Tick each one as you deal with it.</p>
            <ul className="ferro-checkin-interrupt__list" aria-label="Acknowledge before check-in">
              {plan.items.map((item) => {
                const inputId = `checkin-ack-${item.id}`;
                const on = acked.has(item.id);
                return (
                  <li key={item.id}>
                    <label
                      htmlFor={inputId}
                      className={`ferro-checkin-interrupt__item${on ? ' ferro-checkin-interrupt__item--acked' : ''}`}
                      data-testid={`checkin-interrupt-item-${item.flag.key}`}
                    >
                      <input
                        type="checkbox"
                        id={inputId}
                        name={inputId}
                        checked={on}
                        onChange={(e) => toggle(item.id, e.target.checked)}
                        data-testid={`checkin-interrupt-ack-${item.flag.key}`}
                      />
                      <span className="ferro-checkin-interrupt__item-body">
                        <DogFlagChips
                          flags={[item.flag]}
                          scope={item.flag.scope === 'account' ? 'account' : 'dog'}
                          testIdPrefix="checkin-interrupt-"
                        />
                        <span>{itemText(item)}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}

        {plan.heatChecks.map((check) => {
          const answer = heat[check.id];
          const noId = `checkin-heat-${check.id}-no`;
          const yesId = `checkin-heat-${check.id}-yes`;
          return (
            <fieldset
              key={check.id}
              className="ferro-checkin-interrupt__heat"
              data-testid={`checkin-interrupt-heat-${check.dogId}`}
            >
              <legend>Is {check.dogName} in heat?</legend>
              {check.declaration ? (
                <p className="ferro-checkin-interrupt__heat-declared" data-testid={`checkin-interrupt-heat-declared-${check.dogId}`}>
                  Owner declared not in heat{formatDeclaredAt(check.declaration.declaredAt)} — confirm at drop-off.
                </p>
              ) : (
                <p className="ferro-checkin-interrupt__heat-declared">
                  Not spayed, or not recorded — we do not accept females in heat. Ask the owner.
                </p>
              )}
              <div className="ferro-checkin-interrupt__heat-options" role="radiogroup" aria-label={`Is ${check.dogName} in heat?`}>
                <label htmlFor={noId} className="ferro-checkin-interrupt__heat-option ferro-checkin-interrupt__heat-option--no">
                  <input
                    type="radio"
                    id={noId}
                    name={`checkin-heat-${check.id}`}
                    value="no"
                    checked={answer === 'no'}
                    onChange={() => setHeat((h) => ({ ...h, [check.id]: 'no' }))}
                    data-testid={`checkin-interrupt-heat-no-${check.dogId}`}
                  />
                  No — not in heat
                </label>
                <label htmlFor={yesId} className="ferro-checkin-interrupt__heat-option ferro-checkin-interrupt__heat-option--yes">
                  <input
                    type="radio"
                    id={yesId}
                    name={`checkin-heat-${check.id}`}
                    value="yes"
                    checked={answer === 'yes'}
                    onChange={() => setHeat((h) => ({ ...h, [check.id]: 'yes' }))}
                    data-testid={`checkin-interrupt-heat-yes-${check.dogId}`}
                  />
                  Yes — in heat
                </label>
              </div>
              {answer === 'yes' ? (
                <p className="ferro-checkin-interrupt__heat-yes" role="alert" data-testid={`checkin-interrupt-heat-blocked-${check.dogId}`}>
                  <strong>{check.dogName} is not admitted today.</strong> We do not accept females in heat — tell the
                  owner now. The reservation stays as booked for the desk to cancel or move. If she is already in the
                  building, use <em>In heat</em> on her stay to isolate her and arrange pickup.
                </p>
              ) : null}
            </fieldset>
          );
        })}

        <div className="ferro-blocking-dialog__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            ref={cancelRef}
            onClick={onCancel}
            data-testid="checkin-interrupt-cancel"
          >
            {onlyStops ? 'Close' : 'Not now'}
          </button>
          {!onlyStops ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={!canProceed}
              onClick={() => onProceed(outcome)}
              data-testid="checkin-interrupt-proceed"
            >
              {proceedCount === 0
                ? 'Nothing to check in'
                : proceedCount === 1 && plan.rows.length === 1
                  ? 'Check in'
                  : `Check in ${proceedCount}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
